import React, { useState } from "react";
import PropTypes from 'prop-types';
import Checkbox from '@components/common/checkbox/main';

function ActionSelection({
  actionableKeys,
  updateActionableKeys,
  actionableResources,
  ruleType,
  allSelected,
  handleSelectAll,
}) {
  const [actionableSearch, updateActionableSearch] = useState('');
  const renderActionableSearch = () => {
    const hideCloseClass = actionableSearch === '' ? 'hide-close' : '';
    return (
      <div className="add-resource-search-container">
        <span
          className={`icon bff-close ${hideCloseClass}`}
          onClick={() => updateActionableSearch('')}
          role="button"
          tabIndex={0}
        />
        <input
          className="searchbar add-resource-search"
          onChange={(e) => { updateActionableSearch(e.target.value); }}
          placeholder={bfTranslate("Search")}
          type="text"
          value={actionableSearch}
        />
      </div>
    );
  };

  const actionableType = () => {
    if (ruleType.includes("CollectionAssociation")) {
      return "Collection";
    }
    if (ruleType.includes("LabelAssociation")) {
      return "Label";
    }
    if (ruleType.includes("SectionAssociation")) {
      return "Section";
    }
    return undefined;
  };

  const parentCollectionKey = (collectionKey) => (
    actionableResources.find((collection) => collection?.id === collectionKey)?.attributes?.parent_id
  );
  const subcollectionKeys = (parentKey) => (
    actionableResources.filter((collection) => collection?.attributes?.parent_id === parentKey)
      .map((collection) => collection.id)
  );
  const handleActionableUpdates = (key) => {
    let selections = new Set(actionableKeys);
    if (actionableType() === "Section") {
      selections = new Set([key]); // can only select 1
    } else if (selections.has(key)) {
      selections.delete(key);
      if (actionableType() === "Collection") {
        // Remove dependent children if parent removed
        subcollectionKeys(key).forEach((childKey) => selections.delete(childKey));
      }
    } else {
      selections.add(key);
      if (actionableType() === "Collection") {
        // Add parent if child selected
        selections.add(parentCollectionKey(key));
      }
    }
    updateActionableKeys([...selections]);
  };

  const subcollectionsExist = () => (
    actionableType() === "Collection"
      && [...actionableResources].find((collection) => !!collection.attributes.parent_id)
  );

  const formattedCheckboxes = () => {
    const sortedResources = [...actionableResources].sort((a, b) => {
      if (subcollectionsExist()) {
        // group children with parent
        if ((a.attributes.parent_id || a.id) > (b.attributes.parent_id || b.id)) {
          return 1;
        }
        if ((a.attributes.parent_id || a.id) < (b.attributes.parent_id || b.id)) {
          return -1;
        }
      } else if (a.attributes.name.toUpperCase() > b.attributes.name.toUpperCase()) {
        return 1;
      } else if (a.attributes.name.toUpperCase() < b.attributes.name.toUpperCase()) {
        return -1;
      }
      return 0;
    });
    const displayedActionables = [];

    for (let i = 0; i < sortedResources.length; i += 1) {
      const resource = sortedResources[i];
      const subcollectionIndicator = actionableType() === "Collection" && !!resource?.attributes?.parent_id;
      const displayResource = resource.attributes.name.toUpperCase()
        .indexOf(actionableSearch.toUpperCase()) > -1;

      if (displayResource) {
        displayedActionables.push(
          <li key={i} className={`checkbox-container ${subcollectionIndicator ? 'subcollection-item' : ''}`}>
            <Checkbox
              checked={actionableKeys.indexOf(resource.id) > -1}
              labelHtml={<h5>{resource.attributes.name}</h5>}
              size="sm"
              toggle={() => handleActionableUpdates(resource.id)}
            />
          </li>
        );
      }
    }
    return displayedActionables;
  };

  const renderActionableList = () => {
    if (!actionableResources || actionableResources.length < 1) { return null; }

    const collectionIndicator = actionableType() === "Collection";
    const hideAllCheckbox = ruleType.includes("SectionAssociation") || actionableSearch.length;
    const selectAllCollectionsCheckbox = (
      <li key="-1" className="checkbox-container">
        <Checkbox
          checked={allSelected}
          labelHtml={<h5>{bfTranslate('All')}</h5>}
          size="sm"
          toggle={() => handleSelectAll(!allSelected)}
        />
      </li>
    );

    const checkboxes = hideAllCheckbox
      ? [formattedCheckboxes()]
      : [selectAllCollectionsCheckbox, formattedCheckboxes()];

    return (
      <ul className={`resource-list-container fixed-height bf-scroll-element ${collectionIndicator ? 'collections' : ''}`}>
        { checkboxes }
      </ul>
    );
  };

  return (
    <React.Fragment>
      <h3>{`${bfTranslate("Add to")} ${bfTranslate(actionableType())}${actionableType() !== "Section" ? "s" : ""}`}</h3>
      {renderActionableSearch()}
      {renderActionableList()}
    </React.Fragment>
  );
}

ActionSelection.propTypes = {
  ruleType: PropTypes.string.isRequired,
  actionableResources: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      key: PropTypes.string
    })
  ).isRequired,
  actionableKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  updateActionableKeys: PropTypes.func.isRequired,
  allSelected: PropTypes.bool.isRequired,
  handleSelectAll: PropTypes.func.isRequired,
};

export default ActionSelection;
